import {
  UserMenu,
  Logout,
  useGetIdentity,
  usePermissions,
  type UserMenuProps,
} from 'react-admin';
import { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { CaretakerPreference } from './fleet/CaretakerPreference';
import { OwnProfileMenuItem } from './profile/OwnProfileMenuItem';
import { FeatureGate, useFeatureState } from './featureFlags/FeatureGate';
import { PermissionGate } from './PermissionGate';
import { ELEMENT } from './roleCenter/elementKeys';
import { roleLabel } from './users/role';
import { VersionMenuItems } from './featureFlags/VersionSwitch';
import { MyExchangeAccountsMenuItem } from './publications/MyExchangeAccountsMenuItem';
import { GuideMenuItems } from '../guide/GuideMenuItems';
import { isImpersonationTab } from '../session/sessionToken';

/**
 * Menu użytkownika w pasku górnym.
 *
 * **Napisane od nowa, nie przeniesione.** Oryginał z FreightFusion
 * (`admin/CustomUserMenu.jsx`) robił dwie rzeczy, których tu nie chcemy:
 *
 * 1. Liczył skrót MD5 z adresu e-mail użytkownika i budował z niego URL do
 *    gravatar.com. Oznacza to wysyłanie identyfikowalnego skrótu adresu
 *    pracownika klienta do zewnętrznego serwisu przy każdym otwarciu panelu.
 *    Dla dedykowanej instancji to niepotrzebne ryzyko, a przy okazji wymaga
 *    zależności `md5`.
 * 2. Ciągnął własny hak `useAuth` (129 linii) po dane użytkownika i słownik
 *    ról. To repo ma już `authProvider.ts` z `getIdentity` i `getPermissions`,
 *    więc korzystamy z haków react-admina zamiast przenosić drugą warstwę.
 *
 * Zachowane: nazwa użytkownika, rola i wylogowanie. Dołożone (29.09.2026):
 * „Jestem opiekunem FTL” — preferencja przełącznika „Tylko moje pojazdy”,
 * od 01.10.2026 tylko dla ról z planowaniem FTL (trader floty nie widzi).
 * Rola po polsku („Planista FTL (opiekun pojazdów)”), nie identyfikator z bazy.
 * Dołożone (07.10.2026): „Mój profil” — imię i nazwisko, które każdy wpisuje
 * sam; pasek pokazuje je zamiast loginu, gdy są (`authProvider.getIdentity`).
 * Dołożone (12.10.2026, decyzja użytkownika): „Wersja: obecna / nowa” — tylko
 * na środowisku akceptacyjnym, dla każdego zalogowanego (także gościa).
 * Dołożone (16.10.2026, odpowiedzi Marathonu/Euvic, pyt. 18a): „Moje konta
 * giełdowe” — konto imienne tradera, za przełącznikiem
 * `exchangePersonalAccount` i tylko z uprawnieniem Trade.
 * Dołożone (21.10.2026, decyzja użytkownika na prośbę klienta): „Przewodnik po
 * zmianach” (włącz/wyłącz) i „Co nowego” — za przełącznikiem `guideOverlay`.
 */
export const CustomUserMenu = (props: Omit<UserMenuProps, 'children'>) => {
  const { identity, refetch } = useGetIdentity();
  // Karta „Zaloguj jako” (02.11.2026): ustawień konta przejętej osoby się nie
  // zmienia (API odpowiada 403) — pozycje profilu, kont giełdowych, opiekuna
  // FTL i wersji chowamy; „Wyloguj” kończy przejęcie.
  const impersonating = isImpersonationTab();
  const { permissions } = usePermissions<string>();
  // Tożsamość zależy od przełącznika `ownProfile` (bez niego — login, bez
  // pytania o profil; 15.10.2026). Zmiana wersji w trakcie sesji przestawia
  // przełącznik — wtedy czytamy tożsamość od nowa; pierwszy odczyt nie.
  const ownProfileOn = useFeatureState('ownProfile');
  const seenOwnProfile = useRef<boolean | undefined>(undefined);
  useEffect(() => {
    if (ownProfileOn === undefined) return;
    const previous = seenOwnProfile.current;
    seenOwnProfile.current = ownProfileOn;
    if (previous !== undefined && previous !== ownProfileOn) void refetch?.();
  }, [ownProfileOn, refetch]);

  return (
    <UserMenu {...props}>
      <Box sx={{ px: 2, py: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {identity?.fullName ?? 'Użytkownik'}
        </Typography>
        {permissions && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {roleLabel(permissions)}
          </Typography>
        )}
      </Box>
      {/* Imię i nazwisko własnego konta — dla każdego zalogowanego (07.10.2026). */}
      {/* Za przełącznikiem `ownProfile` (12.10.2026) — wyłączony: pozycji nie ma,
          a pasek pokazuje login (API odpowiada 404 na `/users/me`). */}
      {!impersonating && (
        <FeatureGate feature="ownProfile">
          <OwnProfileMenuItem />
        </FeatureGate>
      )}
      {/* Konto imienne tradera do giełd (16.10.2026, pyt. 18a) — tylko zapis. */}
      {!impersonating && (
        <FeatureGate feature="exchangePersonalAccount">
          <PermissionGate permission="TRADE" element={ELEMENT.userMenuExchangeAccount}>
            <MyExchangeAccountsMenuItem />
          </PermissionGate>
        </FeatureGate>
      )}
      {/* Preferencja „Tylko moje pojazdy” (status 29.09.2026); od 07.10.2026
          zawęża też warianty planisty do jego aut. */}
      {!impersonating && (
        <PermissionGate permission="FTL_PLANNING" element={ELEMENT.userMenuCaretaker}>
          <CaretakerPreference />
        </PermissionGate>
      )}
      {/* Wybór wersji (12.10.2026) — sam się ukrywa poza środowiskiem akceptacyjnym. */}
      {!impersonating && <VersionMenuItems />}
      {/* Przewodnik po zmianach (21.10.2026) — włącz/wyłącz i „Co nowego”. */}
      <FeatureGate feature="guideOverlay">
        <GuideMenuItems />
      </FeatureGate>
      <Logout />
    </UserMenu>
  );
};

export default CustomUserMenu;
